/**
 * SettingsPage - Phase 5 Enhanced Settings Management
 * Comprehensive application and user configuration interface
 * 
 * @description Professional settings interface combining:
 * - User profile and account management
 * - Application preferences and themes
 * - Cloud provider configurations
 * - Security and authentication settings
 * - Notification and alert preferences
 * - API keys and integration management
 */

import { useState, useEffect } from "react";
import { useUser, useClerk } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Save,
  Key,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Edit,
  Copy,
  Upload,
  Monitor,
  Sun,
  Moon,
  Shield,
  CheckCircle,
  AlertTriangle,
  Activity,
  Cloud,
  Settings
} from "lucide-react";

// Enhanced settings interfaces
interface UserProfile {
  id: string;
  fullName: string;
  email: string;
  avatar?: string;
  role: 'admin' | 'user' | 'viewer';
  department: string;
  timezone: string;
  language: string;
  dateFormat: string;
  createdAt: string;
  lastLogin: string;
}

interface CloudProvider {
  id: string;
  name: 'aws' | 'gcp' | 'azure' | 'digitalocean' | 'linode';
  displayName: string;
  status: 'connected' | 'disconnected' | 'error';
  region: string;
  accountId: string;
  lastSync: string;
  resourceCount: number;
  isDefault: boolean;
}

interface ApiKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string;
  expiresAt?: string;
  isActive: boolean;
}

interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  alerts: {
    security: boolean;
    costs: boolean;
    performance: boolean;
    workflows: boolean;
  };
  frequency: 'immediate' | 'daily' | 'weekly';
}

interface AppPreferences {
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  defaultProvider: string;
  autoRefresh: boolean;
  refreshInterval: number;
  compactMode: boolean;
  showTooltips: boolean;
}

// Mock data
const mockUserProfile: UserProfile = {
  id: 'user-001',
  fullName: 'John Doe',
  email: 'john.doe@company.com',
  avatar: '',
  role: 'admin',
  department: 'DevOps',
  timezone: 'UTC-8',
  language: 'en-US',
  dateFormat: 'MM/DD/YYYY',
  createdAt: '2024-01-15T09:30:00Z',
  lastLogin: '2024-12-19T10:30:00Z'
};

const mockCloudProviders: CloudProvider[] = [
  {
    id: 'aws-001',
    name: 'aws',
    displayName: 'Amazon Web Services',
    status: 'connected',
    region: 'us-west-2',
    accountId: '123456789012',
    lastSync: '2024-12-19T10:30:00Z',
    resourceCount: 145,
    isDefault: true
  },
  {
    id: 'gcp-001',
    name: 'gcp',
    displayName: 'Google Cloud Platform',
    status: 'connected',
    region: 'us-central1',
    accountId: 'project-12345',
    lastSync: '2024-12-19T09:45:00Z',
    resourceCount: 89,
    isDefault: false
  },
  {
    id: 'azure-001',
    name: 'azure',
    displayName: 'Microsoft Azure',
    status: 'error',
    region: 'eastus',
    accountId: 'sub-67890',
    lastSync: '2024-12-18T14:20:00Z',
    resourceCount: 67,
    isDefault: false
  }
];

const mockApiKeys: ApiKey[] = [
  {
    id: 'api-001',
    name: 'Production API',
    key: 'oc_live_sk_1234567890abcdef',
    permissions: ['read', 'write', 'admin'],
    createdAt: '2024-11-01T10:00:00Z',
    lastUsed: '2024-12-19T08:30:00Z',
    expiresAt: '2025-11-01T10:00:00Z',
    isActive: true
  },
  {
    id: 'api-002',
    name: 'Development API',
    key: 'oc_test_sk_abcdef1234567890',
    permissions: ['read', 'write'],
    createdAt: '2024-10-15T14:30:00Z',
    lastUsed: '2024-12-18T16:45:00Z',
    isActive: true
  }
];

const mockNotificationSettings: NotificationSettings = {
  email: true,
  push: true,
  sms: false,
  alerts: {
    security: true,
    costs: true,
    performance: false,
    workflows: true
  },
  frequency: 'immediate'
};

const mockAppPreferences: AppPreferences = {
  theme: 'system',
  language: 'en-US',
  timezone: 'UTC-8',
  dateFormat: 'MM/DD/YYYY',
  defaultProvider: 'aws',
  autoRefresh: true,
  refreshInterval: 30,
  compactMode: false,
  showTooltips: true
};

export default function SettingsPage() {
  const { user } = useUser();
  const clerk = useClerk();
  const [userProfile, setUserProfile] = useState<UserProfile>(mockUserProfile);
  const [cloudProviders] = useState<CloudProvider[]>(mockCloudProviders);
  const [apiKeys] = useState<ApiKey[]>(mockApiKeys);
  const [notifications, setNotifications] = useState<NotificationSettings>(mockNotificationSettings);
  const [preferences, setPreferences] = useState<AppPreferences>(mockAppPreferences);
  const [showApiKey, setShowApiKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'aws': return <Cloud className="w-5 h-5 text-orange-500" />;
      case 'gcp': return <Cloud className="w-5 h-5 text-blue-500" />;
      case 'azure': return <Cloud className="w-5 h-5 text-blue-600" />;
      case 'digitalocean': return <Cloud className="w-5 h-5 text-blue-400" />;
      case 'linode': return <Cloud className="w-5 h-5 text-green-600" />;
      default: return <Cloud className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800';
      case 'disconnected': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-4 h-4" />;
      case 'disconnected': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  // Sync Clerk user data into profile on mount or Clerk user change
  useEffect(() => {
    if (user) {
      setUserProfile(prev => ({
        ...prev,
        fullName: user.fullName || prev.fullName,
        email: user.primaryEmailAddress?.emailAddress || prev.email,
        avatar: user.imageUrl || prev.avatar,
        // Optionally sync other fields if you store them in Clerk metadata
      }));
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update Clerk user profile
      if (user) {
        // Update basic profile fields (avoid unknown properties that the Clerk types don't accept)
        await user.update({
          firstName: userProfile.fullName.split(' ')[0],
          lastName: userProfile.fullName.split(' ').slice(1).join(' '),
        });
        if (userProfile.email !== user.primaryEmailAddress?.emailAddress) {
          // Clerk UserResource doesn't expose updateEmailAddress in types — create a new email address instead
          // (this preserves runtime behavior while matching the SDK typings)
          // Cast to any to align with runtime shape while satisfying TypeScript
          await user.createEmailAddress({ emailAddress: userProfile.email } as any);
        }
        // Optionally update metadata for department, role, etc.
        // Include avatar in unsafeMetadata to store it without relying on an unknown top-level property
        await user.update({
          unsafeMetadata: {
            department: userProfile.department,
            role: userProfile.role,
            timezone: userProfile.timezone,
            language: userProfile.language,
            dateFormat: userProfile.dateFormat,
            avatar: userProfile.avatar,
          }
        });
      }
      // Simulate API call for local state
      await new Promise(resolve => setTimeout(resolve, 500));
    } finally {
      setSaving(false);
    }
  };

  const toggleApiKeyVisibility = (keyId: string) => {
    setShowApiKey(showApiKey === keyId ? null : keyId);
  };

  const maskApiKey = (key: string) => {
    return key.slice(0, 8) + '...' + key.slice(-8);
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Settings</h2>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Activity className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="api-keys">API Keys</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Profile</CardTitle>
              <CardDescription>Manage your personal information and account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={userProfile.avatar} />
                  <AvatarFallback className="text-lg">
                    {userProfile.fullName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Button size="sm" asChild>
                    <label htmlFor="avatar-upload">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photo
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // Optionally upload to Clerk or your backend
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setUserProfile(prev => ({ ...prev, avatar: ev.target?.result as string }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    JPG, GIF or PNG. 1MB max.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="destructive" onClick={() => clerk.signOut()}>
                  Sign Out
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={userProfile.fullName}
                    onChange={(e) => setUserProfile(prev => ({ ...prev, fullName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userProfile.email}
                    onChange={(e) => setUserProfile(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={userProfile.department}
                    onChange={(e) => setUserProfile(prev => ({ ...prev, department: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={userProfile.role} onValueChange={(value: any) => setUserProfile(prev => ({ ...prev, role: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={userProfile.timezone} onValueChange={(value) => setUserProfile(prev => ({ ...prev, timezone: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC-8">Pacific Time (UTC-8)</SelectItem>
                      <SelectItem value="UTC-5">Eastern Time (UTC-5)</SelectItem>
                      <SelectItem value="UTC+0">UTC</SelectItem>
                      <SelectItem value="UTC+1">Central European Time (UTC+1)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select value={userProfile.language} onValueChange={(value) => setUserProfile(prev => ({ ...prev, language: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="en-GB">English (UK)</SelectItem>
                      <SelectItem value="es-ES">Spanish</SelectItem>
                      <SelectItem value="fr-FR">French</SelectItem>
                      <SelectItem value="de-DE">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Account Created</Label>
                  <p className="text-sm text-muted-foreground">
                    {new Date(userProfile.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Last Login</Label>
                  <p className="text-sm text-muted-foreground">
                    {new Date(userProfile.lastLogin).toLocaleDateString()}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Account Status</Label>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Application Preferences</CardTitle>
              <CardDescription>Customize your application experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Theme</Label>
                    <Select value={preferences.theme} onValueChange={(value: any) => setPreferences(prev => ({ ...prev, theme: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">
                          <div className="flex items-center space-x-2">
                            <Sun className="w-4 h-4" />
                            <span>Light</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="dark">
                          <div className="flex items-center space-x-2">
                            <Moon className="w-4 h-4" />
                            <span>Dark</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="system">
                          <div className="flex items-center space-x-2">
                            <Monitor className="w-4 h-4" />
                            <span>System</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Cloud Provider</Label>
                    <Select value={preferences.defaultProvider} onValueChange={(value) => setPreferences(prev => ({ ...prev, defaultProvider: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aws">Amazon Web Services</SelectItem>
                        <SelectItem value="gcp">Google Cloud Platform</SelectItem>
                        <SelectItem value="azure">Microsoft Azure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Auto Refresh Interval (seconds)</Label>
                    <Select value={preferences.refreshInterval.toString()} onValueChange={(value) => setPreferences(prev => ({ ...prev, refreshInterval: parseInt(value) }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 seconds</SelectItem>
                        <SelectItem value="30">30 seconds</SelectItem>
                        <SelectItem value="60">1 minute</SelectItem>
                        <SelectItem value="300">5 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Auto Refresh</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically refresh dashboard data
                      </p>
                    </div>
                    <Switch
                      checked={preferences.autoRefresh}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, autoRefresh: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Compact Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        Use compact layout for better space utilization
                      </p>
                    </div>
                    <Switch
                      checked={preferences.compactMode}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, compactMode: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Show Tooltips</Label>
                      <p className="text-sm text-muted-foreground">
                        Display helpful tooltips and hints
                      </p>
                    </div>
                    <Switch
                      checked={preferences.showTooltips}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, showTooltips: checked }))}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Cloud Providers</CardTitle>
                  <CardDescription>Manage your cloud provider connections</CardDescription>
                </div>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Provider
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cloudProviders.map((provider) => (
                  <Card key={provider.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {getProviderIcon(provider.name)}
                        <div>
                          <h4 className="font-medium">{provider.displayName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {provider.accountId} • {provider.region}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(provider.status)}
                            <Badge className={getStatusColor(provider.status)}>
                              {provider.status}
                            </Badge>
                            {provider.isDefault && (
                              <Badge variant="outline">Default</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {provider.resourceCount} resources
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Last sync: {new Date(provider.lastSync).toLocaleString()}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>API Keys</CardTitle>
                  <CardDescription>Manage your API keys and access tokens</CardDescription>
                </div>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create API Key
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((apiKey) => (
                    <TableRow key={apiKey.id}>
                      <TableCell className="font-medium">{apiKey.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {showApiKey === apiKey.id ? apiKey.key : maskApiKey(apiKey.key)}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleApiKeyVisibility(apiKey.id)}
                          >
                            {showApiKey === apiKey.id ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigator.clipboard.writeText(apiKey.key)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          {apiKey.permissions.map((permission) => (
                            <Badge key={permission} variant="secondary" className="text-xs">
                              {permission}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(apiKey.lastUsed).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={apiKey.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {apiKey.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete API Key</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this API key? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Configure how you receive alerts and notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Notification Channels</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive notifications via email
                      </p>
                    </div>
                    <Switch
                      checked={notifications.email}
                      onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive browser push notifications
                      </p>
                    </div>
                    <Switch
                      checked={notifications.push}
                      onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, push: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>SMS Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive critical alerts via SMS
                      </p>
                    </div>
                    <Switch
                      checked={notifications.sms}
                      onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, sms: checked }))}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Alert Categories</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Security Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Vulnerability and security incident notifications
                      </p>
                    </div>
                    <Switch
                      checked={notifications.alerts.security}
                      onCheckedChange={(checked) => setNotifications(prev => ({ 
                        ...prev, 
                        alerts: { ...prev.alerts, security: checked }
                      }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Cost Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Budget thresholds and cost optimization opportunities
                      </p>
                    </div>
                    <Switch
                      checked={notifications.alerts.costs}
                      onCheckedChange={(checked) => setNotifications(prev => ({ 
                        ...prev, 
                        alerts: { ...prev.alerts, costs: checked }
                      }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Performance Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Resource performance and availability notifications
                      </p>
                    </div>
                    <Switch
                      checked={notifications.alerts.performance}
                      onCheckedChange={(checked) => setNotifications(prev => ({ 
                        ...prev, 
                        alerts: { ...prev.alerts, performance: checked }
                      }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Workflow Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Automation and workflow execution notifications
                      </p>
                    </div>
                    <Switch
                      checked={notifications.alerts.workflows}
                      onCheckedChange={(checked) => setNotifications(prev => ({ 
                        ...prev, 
                        alerts: { ...prev.alerts, workflows: checked }
                      }))}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Notification Frequency</Label>
                <Select value={notifications.frequency} onValueChange={(value: any) => setNotifications(prev => ({ ...prev, frequency: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate</SelectItem>
                    <SelectItem value="daily">Daily Digest</SelectItem>
                    <SelectItem value="weekly">Weekly Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account security and authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Password & Authentication</h4>
                <div className="space-y-4">
                  <Button variant="outline">
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Two-Factor Authentication</Label>
                      <p className="text-sm text-muted-foreground">
                        Add an extra layer of security to your account
                      </p>
                    </div>
                    <Button variant="outline">
                      <Shield className="w-4 h-4 mr-2" />
                      Enable 2FA
                    </Button>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Sessions</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Current Session</p>
                      <p className="text-xs text-muted-foreground">
                        Chrome on macOS • 192.168.1.100 • Active now
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Current</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Previous Session</p>
                      <p className="text-xs text-muted-foreground">
                        Safari on iPhone • 10.0.1.15 • 2 hours ago
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Revoke
                    </Button>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">
                  Sign Out All Other Sessions
                </Button>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Account Security</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Security Score</Label>
                    <div className="space-y-2">
                      <Progress value={85} className="w-full" />
                      <p className="text-sm text-muted-foreground">
                        Good (85/100) • Enable 2FA to improve
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Last Security Check</Label>
                    <p className="text-sm text-muted-foreground">
                      December 19, 2024 at 10:30 AM
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}